import { OntologyEntity, OntologyRelation } from '../utils/backend-utils';

export interface GraphNode {
  id: string;
  name: string;
  category: string;
  subtype?: string;
  notes?: string;
  aliases?: string[];
}

export interface GraphLink {
  id: string;
  source: string;
  target: string;
  predicate: string;
  evidence?: string;
  notes?: string;
}

export function buildGraphData(
  entities: OntologyEntity[] | undefined,
  relations: OntologyRelation[] | undefined
): { nodes: GraphNode[]; links: GraphLink[] } {
  const nodes: GraphNode[] = (entities || [])
    .filter((e) => e.id && e.name)
    .map((e) => ({
      id: e.id,
      name: e.name,
      category: e.category || 'Other',
      subtype: e.subtype,
      notes: e.notes,
      aliases: e.aliases,
    }));

  const nodeIds = new Set(nodes.map((n) => n.id));
  const links: GraphLink[] = (relations || [])
    .filter((r) => r.source && r.target && nodeIds.has(r.source) && nodeIds.has(r.target))
    .map((r, i) => ({
      id: r.id || `rel-${i}-${r.source}-${r.predicate}-${r.target}`,
      source: r.source,
      target: r.target,
      predicate: r.predicate,
      evidence: r.evidence,
      notes: r.notes,
    }));

  return { nodes, links };
}

/** BFS from focusNodeId up to maxHops; returns set of visible node ids. */
export function nodesWithinHops(
  focusNodeId: string | null,
  links: GraphLink[],
  maxHops: number
): Set<string> | null {
  if (!focusNodeId || maxHops <= 0) return null;

  const adjacency = new Map<string, Set<string>>();
  for (const link of links) {
    adjacency.set(link.source, (adjacency.get(link.source) || new Set()).add(link.target));
    adjacency.set(link.target, (adjacency.get(link.target) || new Set()).add(link.source));
  }

  const visited = new Set<string>([focusNodeId]);
  let frontier = [focusNodeId];

  for (let hop = 0; hop < maxHops && frontier.length > 0; hop += 1) {
    const next: string[] = [];
    for (const nodeId of frontier) {
      const neighbors = adjacency.get(nodeId);
      if (!neighbors) continue;
      neighbors.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.push(neighbor);
        }
      });
    }
    frontier = next;
  }

  return visited;
}

export function filterByCategories(nodes: GraphNode[], enabledCategories: Set<string>): GraphNode[] {
  if (enabledCategories.size === 0) return nodes;
  return nodes.filter((n) => enabledCategories.has(n.category || 'Other'));
}

export function matchSearch(node: GraphNode, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  if (node.name.toLowerCase().includes(q)) return true;
  if (node.id.toLowerCase().includes(q)) return true;
  if (node.subtype?.toLowerCase().includes(q)) return true;
  return (node.aliases || []).some((a) => a.toLowerCase().includes(q));
}
